import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { Link, Navigate, useParams } from "react-router-dom"
import { isAxiosError } from "axios"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import {
  listReportsApi,
  resolveReportApi,
  Report,
  ReportStatus,
  TargetType,
  ResolveAction,
} from "../../features/reports/api/reports.api"
import { REPORT_TYPE_MAP, STATUS_CONFIG, isReportType } from "./reportTypes"
import { FilterDropdown } from "../../shared/ui/FilterDropdown"
import {
  FlagIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  DocumentTextIcon,
  ShieldExclamationIcon,
  ArrowTopRightOnSquareIcon,
  NoSymbolIcon,
  ChevronLeftIcon,
} from "@heroicons/react/16/solid"
import { XMarkIcon } from "@heroicons/react/20/solid"

const LIMIT = 20

/* ── Where to send admin to look at the reported entity.
   Only courses have a real per-id admin page; the rest fall back to their list page. ── */
const TARGET_LINK: Record<TargetType, { to: (id: string) => string; label: string }> = {
  course: { to: (id) => `/courses/${id}`, label: "Открыть курс" },
  user:   { to: () => `/users`,             label: "Открыть в разделе Пользователи" },
  job:    { to: () => `/vacancies`,         label: "Открыть в разделе Вакансии" },
  china:  { to: () => `/china-enterprises`, label: "Открыть в разделе Китайские предприятия" },
}

const StatusBadge = ({ status }: { status: ReportStatus }) => {
  const cfg = STATUS_CONFIG[status]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.icon}
      {cfg.label}
    </span>
  )
}

const TargetLink = ({ type, id, className }: { type: TargetType; id: string; className?: string }) => {
  const cfg = TARGET_LINK[type]
  return (
    <Link
      to={cfg.to(id)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={className ?? "inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"}
    >
      {cfg.label}
      <ArrowTopRightOnSquareIcon className="w-3 h-3" />
    </Link>
  )
}

const formatDate = (ts: number) =>
  new Date(ts * 1000).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })

type Toast = { text: string; variant: "success" | "error" }

export const ReportsListPage = () => {
  const { type } = useParams<{ type: string }>()

  if (!type || !isReportType(type)) {
    return <Navigate to="/reports" replace />
  }

  return <ReportsListPageInner targetType={type} />
}

const ReportsListPageInner = ({ targetType }: { targetType: TargetType }) => {
  const meta = REPORT_TYPE_MAP[targetType]

  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)

  const [filterStatus, setFilterStatus] = useState<ReportStatus | "">("pending")

  const [selected, setSelected] = useState<Report | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [noteText, setNoteText] = useState("")

  const [toast, setToast] = useState<Toast | null>(null)

  const showToast = (text: string, variant: Toast["variant"] = "success") => {
    setToast({ text, variant })
    setTimeout(() => setToast((t) => (t?.text === text ? null : t)), 4000)
  }

  const load = async () => {
    setLoading(true)
    const offset = (page - 1) * LIMIT
    const res = await listReportsApi({
      limit: LIMIT,
      offset,
      status: filterStatus || undefined,
      target_type: targetType,
    })
    setReports(res.data.items)
    setTotal(res.data.total)
    setLoading(false)
  }

  useEffect(() => { load() }, [page, filterStatus, targetType]) // eslint-disable-line

  useEffect(() => { setPage(1) }, [filterStatus, targetType])

  const openReport = (r: Report) => {
    setSelected(r)
    setNoteText(r.admin_note ?? "")
  }

  const handleResolve = async (action: ResolveAction) => {
    if (!selected) return

    if (action === "deactivate") {
      const ok = window.confirm(
        "Сущность будет деактивирована/заблокирована, а жалоба закрыта. Продолжить?"
      )
      if (!ok) return
    }

    setActionLoading(true)
    try {
      await resolveReportApi(selected.id, { action, admin_note: noteText })
      showToast(
        action === "deactivate" ? "Сущность деактивирована, жалоба закрыта" : "Жалоба отклонена",
        "success"
      )
      setSelected(null)
      await load()
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        showToast("Сущность не найдена, возможно уже удалена", "error")
      } else {
        showToast("Не удалось обработать жалобу", "error")
      }
    } finally {
      setActionLoading(false)
    }
  }

  const totalPages = Math.ceil(total / LIMIT)

  return (
    <DashboardLayout title="Жалобы">
      {/* ── Breadcrumb / heading ── */}
      <div className="flex items-center gap-3 mb-5">
        <Link
          to="/reports"
          className="w-8 h-8 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center flex-shrink-0 transition-colors"
        >
          <ChevronLeftIcon className="w-4 h-4 text-gray-500" />
        </Link>
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.color}`}>
          {meta.icon}
        </span>
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <Link to="/reports" className="hover:text-gray-600 transition-colors">Жалобы</Link>
            <span>/</span>
            <span className="text-gray-500">{meta.label}</span>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 leading-tight">{meta.label}</h2>
        </div>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">

        {/* ── Header / Filters ── */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b bg-gray-50/60">
          <FunnelIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />

          {/* Status filter */}
          <FilterDropdown
            label="Статус"
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { value: "pending", label: "Ожидает", dot: "bg-yellow-400" },
              { value: "reviewed", label: "Рассмотрено", dot: "bg-blue-400" },
              { value: "resolved", label: "Решено", dot: "bg-emerald-400" },
              { value: "dismissed", label: "Отклонено", dot: "bg-gray-400" },
            ]}
          />

          <span className="ml-auto text-xs text-gray-400">
            {!loading && `${total} жалоб`}
          </span>
        </div>

        {/* ── Table ── */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/40">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Объект</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Причина</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Описание</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Статус</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Дата</th>
                <th className="px-5 py-3 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {[1, 2, 3, 4, 5, 6].map((j) => (
                      <td key={j} className="px-5 py-4">
                        <div className="h-4 bg-gray-100 rounded animate-pulse w-24" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-16 text-center">
                    <FlagIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Жалоб не найдено</p>
                  </td>
                </tr>
              ) : (
                reports.map((r) => (
                  <tr key={r.id} onClick={() => openReport(r)} className="hover:bg-gray-50/60 transition-colors group cursor-pointer">
                    <td className="px-5 py-3.5">
                      <div className="flex flex-col gap-1">
                        <span className="font-mono text-xs text-gray-400">{r.target_id.slice(0, 12)}…</span>
                        <TargetLink type={r.target_type} id={r.target_id} />
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-gray-700 max-w-[160px] truncate" title={r.reason}>
                      {r.reason}
                    </td>
                    <td className="px-5 py-3.5 text-gray-500 max-w-[220px] truncate" title={r.description}>
                      {r.description || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-3.5">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-5 py-3.5 text-xs text-gray-400">
                      {formatDate(r.created_at)}
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={(e) => { e.stopPropagation(); openReport(r) }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-blue-600 transition-colors px-2 py-1.5 rounded-lg hover:bg-blue-50 opacity-0 group-hover:opacity-100"
                      >
                        <EyeIcon className="w-3.5 h-3.5" />
                        Открыть
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50/40">
            <span className="text-xs text-gray-400">
              Страница <span className="font-medium text-gray-600">{page}</span> из{" "}
              <span className="font-medium text-gray-600">{totalPages || 1}</span>
            </span>
            <div className="flex gap-1.5">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Назад
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Вперёд →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Detail Drawer ── */}
      <ReportDrawer
        report={selected}
        onClose={() => setSelected(null)}
        noteText={noteText}
        setNoteText={setNoteText}
        actionLoading={actionLoading}
        onResolve={handleResolve}
      />

      {/* ── Toast ── */}
      {toast && (
        <div
          className={`fixed top-5 right-5 z-[60] flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium shadow-lg ${
            toast.variant === "success"
              ? "bg-emerald-600 text-white"
              : "bg-red-600 text-white"
          }`}
        >
          {toast.variant === "success"
            ? <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
            : <XCircleIcon className="w-4 h-4 flex-shrink-0" />
          }
          {toast.text}
        </div>
      )}
    </DashboardLayout>
  )
}

/* ── Report Drawer ── */
type DrawerProps = {
  report: Report | null
  onClose: () => void
  noteText: string
  setNoteText: (v: string) => void
  actionLoading: boolean
  onResolve: (action: ResolveAction) => void
}

const HEADER_COLOR: Record<ReportStatus, string> = {
  pending:   "from-yellow-500 to-amber-400",
  reviewed:  "from-blue-500 to-blue-400",
  resolved:  "from-emerald-500 to-green-400",
  dismissed: "from-gray-400 to-gray-300",
}

const ReportDrawer = ({
  report,
  onClose,
  noteText,
  setNoteText,
  actionLoading,
  onResolve,
}: DrawerProps) => {
  const open = !!report
  const canAct = report ? report.status === "pending" || report.status === "reviewed" : false

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-lg bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {!report ? null : (
          <>
            {/* ── Colored header ── */}
            <div className={`bg-gradient-to-r ${HEADER_COLOR[report.status]} px-6 pt-6 pb-8 relative flex-shrink-0`}>
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              >
                <XMarkIcon className="w-4 h-4 text-white" />
              </button>

              <div className="flex items-center gap-2 mb-3">
                <ShieldExclamationIcon className="w-5 h-5 text-white/80" />
                <span className="text-white/80 text-sm font-medium uppercase tracking-wider">Жалоба</span>
              </div>

              <p className="text-white font-semibold text-lg leading-snug truncate">
                {report.reason}
              </p>

              <div className="flex items-center gap-2 mt-3">
                <StatusBadge status={report.status} />
              </div>
            </div>

            {/* ── Scrollable body ── */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

              {/* IDs card */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Участники</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Заявитель</span>
                  <span className="font-mono text-xs text-gray-600 bg-white border border-gray-200 px-2 py-1 rounded-lg">{report.reporter_id.slice(0, 16)}…</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">ID объекта</span>
                  <span className="font-mono text-xs text-gray-600 bg-white border border-gray-200 px-2 py-1 rounded-lg">{report.target_id.slice(0, 16)}…</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Объект</span>
                  <TargetLink type={report.target_type} id={report.target_id} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Дата</span>
                  <span className="text-xs text-gray-600">{formatDate(report.created_at)}</span>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <DocumentTextIcon className="w-3.5 h-3.5" />
                  Описание жалобы
                </p>
                <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-4 leading-relaxed border border-gray-100">
                  {report.description || <span className="text-gray-300">Без описания</span>}
                </p>
              </div>

              {/* Admin note */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Заметка администратора</p>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={3}
                  placeholder="Добавьте заметку для команды..."
                  disabled={!canAct}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none placeholder:text-gray-300 disabled:bg-gray-50 disabled:text-gray-400"
                />
              </div>

              {!canAct && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm bg-gray-50 text-gray-500 border border-gray-100">
                  <ShieldExclamationIcon className="w-4 h-4 flex-shrink-0" />
                  Жалоба уже обработана ({STATUS_CONFIG[report.status].label})
                </div>
              )}
            </div>

            {/* ── Sticky footer ── */}
            <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50/80 flex gap-2">
              <button
                disabled={actionLoading || !canAct}
                onClick={() => onResolve("dismiss")}
                className="flex-1 flex items-center justify-center gap-2 border border-gray-200 disabled:opacity-40 text-gray-600 hover:bg-white py-2.5 rounded-xl text-sm font-semibold transition-colors"
              >
                {actionLoading
                  ? <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
                  : <XCircleIcon className="w-4 h-4" />
                }
                Отклонить жалобу
              </button>
              <button
                disabled={actionLoading || !canAct}
                onClick={() => onResolve("deactivate")}
                className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-red-200"
              >
                {actionLoading
                  ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <NoSymbolIcon className="w-4 h-4" />
                }
                Деактивировать и закрыть
              </button>
            </div>
          </>
        )}
      </div>
    </>,
    document.body
  )
}